import { trackException, trackExceptionExcludingCanceled } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { IGetActivityFeedQuery } from 'app/models/ActivityFeed/IGetActivityFeed';
import {
  IParentTaskSearchResponse,
  ITaskSearchQuery,
  ITasksList,
  IGridPaging,
} from 'app/models/Tasks/Tasks';
import { ICreateTaskRequest } from 'app/models/Tasks/CreateTaskV1';
import {
  ILoopOutTaskChainRequest,
  ILoopOutTaskChainResponse,
  ITaskChainItem,
  IUpdateTaskChainRequest,
  IUpdateTaskChainStatusRequest,
  IUpdateTaskChainStatusResponse,
} from 'app/models/Tasks/TaskChain';
import { AxiosResponse } from 'axios';
import baseAxiosInstance from 'utils/BaseApi';
import { httpSuccess } from '../serviceHelpers';

const taskBase = globalThis?.app?.env?.REACT_APP_TASKS_SERVICE_BASE_URL;
const sharedBase = globalThis?.app?.env?.REACT_APP_TRACK_CHANGE_SERVICE_BASE_URL;
const notificationBaseUrl = globalThis?.app?.env?.REACT_APP_NOTIFICATION_SERVICE_BASE_URL;

export async function saveTaskMetaData(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/save`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'saveTaskMetaData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function saveExternalTaskMetaData(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/external-task/create`, body);
    if (!httpSuccess(response.status)) {
      return -1;
    }
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'saveExternalTaskMetaData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

/**
 * Create task via v1 API (new Create Task modal). Form-data: request (JSON) + attachments (files).
 * Does not modify existing save flow.
 */
export async function createTaskV1(payload: {
  request: ICreateTaskRequest;
  attachments: File[];
}): Promise<AxiosResponse<number>> {
  try {
    const formData = new FormData();
    formData.append('request', JSON.stringify(payload.request));
    (payload.attachments || []).forEach(file => {
      if (file) formData.append('attachments', file);
    });
    const response = await baseAxiosInstance.post<number>(`${taskBase}/tasks`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'createTaskV1',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function editTaskMetaData(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/update`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'editTaskMetaData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getTaskChain(taskId: number): Promise<AxiosResponse<ITaskChainItem>> {
  try {
    const response = await baseAxiosInstance.get<ITaskChainItem>(`${taskBase}/tasks/${taskId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskChain',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function updateTaskChain(body: IUpdateTaskChainRequest): Promise<AxiosResponse<number>> {
  try {
    const response = await baseAxiosInstance.put<number>(`${taskBase}/tasks`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'updateTaskChain',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function updateTaskChainStatus(
  body: IUpdateTaskChainStatusRequest,
): Promise<AxiosResponse<IUpdateTaskChainStatusResponse>> {
  try {
    const response = await baseAxiosInstance.put<IUpdateTaskChainStatusResponse>(`${taskBase}/tasks/status`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'updateTaskChainStatus',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function deleteTaskChainAttachment(attachmentId: number): Promise<AxiosResponse<boolean>> {
  try {
    const response = await baseAxiosInstance.delete<boolean>(`${taskBase}/tasks/attachments/${attachmentId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'deleteTaskChainAttachment',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function deleteTaskChainTask(taskId: number): Promise<AxiosResponse<void>> {
  try {
    const response = await baseAxiosInstance.delete<void>(`${taskBase}/tasks/${taskId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'deleteTaskChainTask',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function loopOutTaskChain(
  taskId: number,
  body: ILoopOutTaskChainRequest,
): Promise<AxiosResponse<ILoopOutTaskChainResponse>> {
  try {
    const response = await baseAxiosInstance.post<ILoopOutTaskChainResponse>(`${taskBase}/tasks/${taskId}/loop-out`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'loopOutTaskChain',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskDetailsByPlacementId(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/list-byplacement`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskDetailsByPlacementId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskDetails(taskId) {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/task/details/${taskId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskDetails',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

// #region Subtasks

export async function getComments(rootTaskId: number) {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/comment/list/${rootTaskId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getComments',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function saveComment(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/comment/save`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'saveComment',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

// #endregion

export async function getPlacementEmployees(placementId: string) {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/task/placement-employees/${placementId}`);
    if (!httpSuccess(response.status)) {
      return [];
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getPlacementEmployees',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return [];
  }
}

export async function getAllEmployees(searchKey: string) {
  try {
    const response = await baseAxiosInstance.get(`${sharedBase}/search/active-employees/${searchKey}`);
    if (!httpSuccess(response.status)) {
      return [];
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getAllEmployees',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return [];
  }
}

export async function getCategoryList() {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/task/category-tasks`);
    if (!httpSuccess(response.status)) {
      return [];
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getCategoryList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}
export async function getExternalCategoryList() {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/external-task/category-tasks`);
    if (!httpSuccess(response.status)) {
      return [];
    }
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getExternalCategoryList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getStatusList() {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/task/statuses`);
    if (!httpSuccess(response.status)) {
      return [];
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getStatusList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getTaskReadStatus(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/read`, body);
    if (!httpSuccess(response.status)) {
      return null;
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskReadStatus',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function updateTaskStatus(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/update-status`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'updateTaskStatus',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getTaskDetailsByCandidateId(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/list-bycandidate`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskDetailsByCandidateId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskDetailsByFacilityId(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/list-byfacility`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskDetailsByFacilityId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskData(body, cancelToken) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task-management/tasks`, body, { cancelToken });
    return response;
  } catch (error) {
    trackExceptionExcludingCanceled({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskDetailsByOrderId(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/list-byorder`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskDetailsByOrderId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function getTaskGroupedList(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task-management/groups`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskGroupedList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
  }
}
export async function getTaskGridFilters(filters: any) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task-management/filters`, filters);
    if (!httpSuccess(response.status)) {
      return -1;
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskGridFilters',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

// #region Notifications
export async function postNotification(body) {
  try {
    const response = await baseAxiosInstance.post(`${notificationBaseUrl}/alert/publish`, body);
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'postNotification',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
  }
}

export async function GetAlertEmailData(id: any) {
  try {
    const response = await baseAxiosInstance.get(`${notificationBaseUrl}/alert/email/${id}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'GetAlertEmailData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function UpdateAlertEmailData(id: any, body: any) {
  try {
    const response = await baseAxiosInstance.patch(`${notificationBaseUrl}/alert/email/${id}`, body);
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'UpdateAlertEmailData',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
  }
}

// #endregion

export async function DeleteAttachments(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/delete-attachment`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'DeleteAttachments',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function GetExternalTaskList(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/external-task/list`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'GetExternalTaskList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function UpdateExternalTaskStatus(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/external-task/update-status`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'UpdateExternalTaskStatus',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getExternalTaskDetails(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/external-task/details`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getExternalTaskDetails',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

/**
 * Get Task Kpi .
 * @constructor
 * @param {object} payload - Payload specifying user info, channel and period to fetch KPI metrics.
 */
export async function getTaskKpi(payload: {
  userId: string;
  channel?: string[];
  startDate?: Date | string | null;
  endDate?: Date | string | null;
}) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/kpi/tasks`, payload);
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTaskKpi',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

export async function updateExternalTask(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/external-task/update`, body);
    if (!httpSuccess(response.status)) {
      return -1;
    }
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'updateExternalTask',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getExternalTaskSubCategory(taskNameId: number) {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/external-task/task-name/${taskNameId}/subnames`);
    if (!httpSuccess(response.status)) {
      return -1;
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getExternalTaskSubCategory',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

// #region Cue Notes

export async function getCueNoteRecipient(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/cue-note/recipients`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getCueNoteRecipient',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}
export async function getCueNotesByPlacementId(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/cue-note/list-byplacement`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getCueNotesByPlacementId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}
export async function createCueNote(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/cue-note/create`, body);
    if (!httpSuccess(response.status)) {
      return -1;
    }
    return response.data;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'createCueNote',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function saveTaskComment(body) {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/task/task-comment/save`, body);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'saveTaskComment',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    return error;
  }
}

export async function getCueNotesCountByPlacementId(placementId: number) {
  try {
    const response = await baseAxiosInstance.get(`${taskBase}/cue-note/count/${placementId}`);
    return response;
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getCueNotesCountByPlacementId',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

function mapTaskSearchCandidateFromApi(candidate: any) {
  if (!candidate) {
    return { candidateID: 0, candidateName: '', brands: [] };
  }
  return {
    candidateID: candidate.id ?? 0,
    candidateName: candidate.name ?? '',
    brands: Array.isArray(candidate.brands)
      ? candidate.brands.map((brand: any) => ({
          brandId: brand.brandId ?? 0,
          brand: brand.brand ?? '',
        }))
      : [],
  };
}

function mapTaskSearchItemFromApi(item: any): any {
  if (!item) return item;
  const senderName =
    item.sender != null
      ? [item.sender.firstName, item.sender.lastName].filter(Boolean).join(' ').trim() || ''
      : '';
  return {
    rootTaskID: item.rootTaskId ?? 0,
    taskID: item.taskId ?? 0,
    contractAffiliateID: item.contractAffiliateId ?? 0,
    placementID: item.placementId ?? 0,
    orderID: item.orderId ?? 0,
    facility: item.facility
      ? { facilityId: item.facility.id, facilityName: item.facility.name ?? '' }
      : { facilityId: 0, facilityName: '' },
    unit: item.unit
      ? { unitId: item.unit.id, unitName: item.unit.name ?? '' }
      : { unitId: 0, unitName: '' },
    candidate: mapTaskSearchCandidateFromApi(item.candidate),
    isFlagged: item.isFlagged,
    isCoverage: item.isCoverage,
    isOptionApproval: item.isOptionApproval,
    isFollowup: item.isFollowup,
    followupDate: item.followupDate,
    recipients: item.recipients ?? [],
    senderName,
    sentDate: item.sentDate,
    subject: item.subject ?? '',
    text: item.text ?? '',
    priority: item.priority ?? '',
    category: item.category ?? '',
    status: item.status?.name ?? '',
    statusId: item.status?.id,
    coveredEmployee: item.coveredEmployee
      ? { employeeID: item.coveredEmployee.id, employeeName: item.coveredEmployee.name ?? '' }
      : { employeeName: '' },
    ...(item.originalTaskId !== undefined && { originalTaskID: item.originalTaskId }),
    ...(item.subTasks && {
      subTasks: item.subTasks.map((st: any) => mapTaskSearchItemFromApi(st)),
    }),
  };
}

export async function getTasksList(body: ITaskSearchQuery): Promise<ITasksList> {
  try {
    const response = await baseAxiosInstance.post(`${taskBase}/search`, body);
    const data = response?.data;
    const paging = data.paging
      ? {
          pageSize: data.paging.pageSize ?? 0,
          pageNumber: data.paging.pageNumber ?? 1,
          total: data.paging.total ?? 0,
        }
      : undefined;
    const items = Array.isArray(data.items)
      ? data.items.map((item: any) => mapTaskSearchItemFromApi(item))
      : [];
    return { paging, items };
 
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'getTasksList',
        area: 'src/app/services/TaskServices/TaskServices.ts',
      },
    });
    throw error;
  }
}

// #endregion
